import { UseQueryOptions } from '@tanstack/react-query';
import { FEATURE_FLAGS } from '../consts/features';
import { GetUserResponse } from '../network/httpClients/PlatformClient/api.schema';
import { useUserQuery } from '../network/queries';
import { useIsFeatureEnabledQuery } from '../network/queries';
import { isMtWizardEnabled, useDeploymentConfig } from '../services/config';
import {
  canInviteMembers,
  canUserCreateTeam,
  doesUserBelongsToTeam,
  getUserTrialStatus,
  isEffectiveConfigAdmin,
  isEnterpriseUser,
  isFreeUser,
  isOrganizationOwner,
  isPayingTeamUser,
  isTeamOwner,
  isTierScopedOwner,
  isUserPaying,
} from '../utils/user';

/**
 * Hook to access user data and user-related status
 * @returns User data and status information
 */
export const useUser = (queryOptions?: UseQueryOptions<GetUserResponse>) => {
  const { data: user, ...restQueryResult } = useUserQuery(queryOptions);
  const config = useDeploymentConfig();
  const isWizardUnrestricted = !isMtWizardEnabled(config);
  const {
    data: isAzureIntegrationEnabled,
    isLoading: isAzureFeatureFlagLoading,
  } = useIsFeatureEnabledQuery(FEATURE_FLAGS.AZURE_INTEGRATION);
  const {
    data: isGitlabIntegrationEnabled,
    isPending: isGitlabIntegrationFeatureFlagPending,
  } = useIsFeatureEnabledQuery(FEATURE_FLAGS.GITLAB_INTEGRATION);
  const {
    data: isGitlabMTCloudIntegrationEnabled,
    isPending: isGitlabMTCloudIntegrationFeatureFlagPending,
  } = useIsFeatureEnabledQuery(FEATURE_FLAGS.GITLAB_MT_CLOUD_INTEGRATION);
  const {
    data: isBitbucketIntegrationEnabled,
    isLoading: isBitbucketIntegrationFeatureFlagLoading,
  } = useIsFeatureEnabledQuery(FEATURE_FLAGS.BITBUCKET_INTEGRATION);
  const {
    data: isGerritIntegrationEnabled,
    isLoading: isGerritIntegrationFeatureFlagLoading,
  } = useIsFeatureEnabledQuery(FEATURE_FLAGS.GERRIT_INTEGRATION);
  const {
    data: isGithubCloudWizardEnabled,
    isLoading: isGithubCloudWizardLoading,
  } = useIsFeatureEnabledQuery(FEATURE_FLAGS.GITHUB_CLOUD_WIZARD);
  const {
    data: isGithubServerWizardEnabled,
    isLoading: isGithubServerWizardLoading,
  } = useIsFeatureEnabledQuery(FEATURE_FLAGS.GITHUB_SERVER_WIZARD);
  const { data: isGitlabWizardV2Enabled, isPending: isGitlabWizardV2Pending } =
    useIsFeatureEnabledQuery(FEATURE_FLAGS.GITLAB_WIZARD_V2);

  const { isTrialUser, dayLeftForTrial } = getUserTrialStatus(user);

  return {
    user,
    isTrialUser,
    dayLeftForTrial,
    isFreeUser: isFreeUser(user),
    isUserPaying: isUserPaying(user),
    isOrganizationOwner: isOrganizationOwner(user),
    isTeamOwner: isTeamOwner(user),
    doesUserBelongsToTeam: doesUserBelongsToTeam(user),
    isEnterpriseUser: isEnterpriseUser(user),
    isBillingDashboardEnabled:
      isEnterpriseUser(user) && !!user?.billingDashboardEnabled,
    isPayingTeamUser: isPayingTeamUser(user),
    isTierScopedOwner: isTierScopedOwner(user),
    isDelegatedTeamAdmin: user?.isDelegatedTeamAdmin ?? false,
    canEditAdminPages: user?.canEditAdminPages ?? false,
    isEffectiveConfigAdmin: isEffectiveConfigAdmin(user),
    canInviteMembers: canInviteMembers(user),
    canUserCreateTeam: canUserCreateTeam(user),
    hasGithubCloudWizardAccess:
      isWizardUnrestricted || !!isGithubCloudWizardEnabled,
    hasGithubServerWizardAccess:
      isWizardUnrestricted || !!isGithubServerWizardEnabled,
    isGithubCloudWizardEnabled: !!isGithubCloudWizardEnabled,
    isGithubCloudWizardLoading,
    isGithubServerWizardEnabled: !!isGithubServerWizardEnabled,
    isGithubServerWizardLoading,
    isGithubPublicAppInstallationWizardAvailable:
      !!isGithubCloudWizardEnabled && !isGithubCloudWizardLoading,
    isGithubPublicAppInstallationWizardLoading: isGithubCloudWizardLoading,
    isAzureInstallationWizardAvailable: isAzureIntegrationEnabled,
    isAzureInstallationWizardLoading: isAzureFeatureFlagLoading,
    isGitlabInstallationWizardAvailable: isGitlabIntegrationEnabled,
    isGitlabInstallationWizardLoading: isGitlabIntegrationFeatureFlagPending,
    isGitlabWizardV2Enabled: !!isGitlabWizardV2Enabled,
    isGitlabWizardV2Loading: isGitlabWizardV2Pending,
    isGitlabMTCloudInstallationWizardAvailable:
      isGitlabMTCloudIntegrationEnabled,
    isGitlabMTCloudInstallationWizardLoading:
      isGitlabMTCloudIntegrationFeatureFlagPending,
    hasGitlabSaasWizardAccess: isWizardUnrestricted
      ? !!isGitlabIntegrationEnabled
      : !!isGitlabMTCloudIntegrationEnabled,
    isGitlabSaasWizardLoading: isWizardUnrestricted
      ? isGitlabIntegrationFeatureFlagPending
      : isGitlabMTCloudIntegrationFeatureFlagPending,
    isBitbucketInstallationWizardAvailable: isBitbucketIntegrationEnabled,
    isBitbucketInstallationWizardLoading:
      isBitbucketIntegrationFeatureFlagLoading,
    isGerritInstallationWizardAvailable: isGerritIntegrationEnabled,
    isGerritInstallationWizardLoading: isGerritIntegrationFeatureFlagLoading,
    ...restQueryResult,
  };
};
